import { useState, useContext, useEffect, useCallback } from "react";
import {
    Table,
    Input,
    DatePicker,
    Select,
    Row,
    Col,
    Button,
    Tooltip,
    Dropdown,
    Checkbox,
    Modal,
    Descriptions,
} from "antd";
import {
    FileExcelOutlined,
    ColumnHeightOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { AuthContext } from "../context/AuthContext";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Image } from "antd";

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function TeamAttendance({ isHomePage }) {
    const { authData, backUrl } = useContext(AuthContext);

    const [normalizedMembers, setNormalizedMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 30 });

    const [filters, setFilters] = useState({
        name: "",
        empCode: "",
        status: "",
        organisation: "",
        department: "",
        designation: "",
        fromDate: null,
        toDate: null,
        remarks: "",
    });

    const formatRemarks = (remarks) => {
        if (!remarks) return "–";
        if (remarks.includes("Grace - Early Out")) return "Early Out";
        if (remarks.includes("Grace - Late In")) return "Late In";
        return remarks;
    };

    const allColumns = [
        { key: "EmployeeCode", title: "Employee Code", defaultVisible: false },
        { key: "EmployeeName", title: "Employee Name", defaultVisible: true },
        { key: "Organisation", title: "Organisation", defaultVisible: false },
        { key: "Department", title: "Department", defaultVisible: false },
        { key: "Designation", title: "Designation", defaultVisible: false },
        { key: "Date", title: "Date", defaultVisible: true },
        { key: "AttendanceStatus", title: "Status", defaultVisible: true },
        { key: "Remarks", title: "Remarks", defaultVisible: true },
        { key: "site_location", title: "Work Location", defaultVisible: false },
        { key: "PunchIn", title: "Punch In", defaultVisible: true },
        { key: "CheckIn", title: "Check In", defaultVisible: true },
        { key: "check_in_photo_url", title: "Check In Photo", defaultVisible: true },
        { key: "CheckInLocation", title: "Check In Location", defaultVisible: true },
        { key: "CheckInWorkSite", title: "Check In Site", defaultVisible: true },
        { key: "PunchOut", title: "Punch Out", defaultVisible: true },
        { key: "CheckOut", title: "Check Out", defaultVisible: true },
        { key: "check_out_photo_url", title: "Check Out Photo", defaultVisible: true },
        { key: "CheckOutLocation", title: "Check Out Location", defaultVisible: true },
        { key: "CheckOutWorkSite", title: "Check Out Site", defaultVisible: true },
        { key: "FirstInType", title: "First In", defaultVisible: true },
        { key: "LastOutType", title: "Last Out", defaultVisible: true },
    ];

    const [visibleColumns, setVisibleColumns] = useState(
        allColumns.filter((col) => col.defaultVisible).map((col) => col.key)
    );

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results || []);
        } catch (err) {
            console.error("Failed to fetch organisations", err);
        }
    };

    const fetchDepartments = async (orgId) => {
        if (!orgId) { setDepartments([]); return; }
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDepartments(res.data.results || []);
        } catch (err) {
            console.error("Failed to fetch departments", err);
        }
    };

    const fetchDesignations = async (deptId) => {
        if (!deptId) { setDesignations([]); return; }
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDesignations(res.data.results || []);
        } catch (err) {
            console.error("Failed to fetch designations", err);
        }
    };

    const buildQueryParams = useCallback((filterValues) => {
        const params = new URLSearchParams();
        params.append("is_employee_view", "true");

        if (filterValues.name) params.append("emp_name", filterValues.name);
        if (filterValues.empCode) params.append("emp_code", filterValues.empCode);
        if (filterValues.status) params.append("status", filterValues.status);
        if (filterValues.organisation) params.append("organisation", filterValues.organisation);
        if (filterValues.department) params.append("department", filterValues.department);
        if (filterValues.designation) params.append("designation", filterValues.designation);
        if (filterValues.fromDate) params.append("from_date", filterValues.fromDate.format("YYYY-MM-DD"));
        if (filterValues.toDate) params.append("to_date", filterValues.toDate.format("YYYY-MM-DD"));
        // Only send remarks param if status is "present" and a remark is selected
        if (filterValues.status === "present" && filterValues.remarks) {
            params.append("remarks", filterValues.remarks);
        }

        return params.toString();
    }, []);

    const fetchAttendanceData = useCallback(async (filterValues = filters) => {
        if (!authData?.employee?.id) return;

        try {
            setLoading(true);
            const queryString = buildQueryParams(filterValues);
            const res = await axios.get(
                `${backUrl}/api/hr/attendance/hod/?${queryString}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            const raw = res.data.results || [];

            const cleaned = raw.map((item) => ({
                EmployeeCode: item.EmployeeCode || item.employee_code || item.employee?.emp_code || "",
                EmployeeName:
                    item.EmployeeName ||
                    item.employee_name ||
                    `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`.trim(),
                Organisation: item.Organisation || item.organisation || "",
                Department: item.Department || item.department || "",
                Designation: item.Designation || item.designation || "",
                Date: item.Date || item.date || "",
                PunchIn: item.PunchIn || item.punch_in || "",
                PunchOut: item.PunchOut || item.punch_out || "",
                CheckIn: item.CheckIn || item.check_in || "",
                AttendanceStatus: item.AttendanceStatus || item.attendance_status || "",
                site_location: item.site_location || "",
                check_in_photo_url: item.check_in_photo_url || "",
                CheckInLocation: item.CheckInLocation || "",
                CheckInWorkSite: item.CheckInWorkSite || "",
                CheckOut: item.CheckOut || "",
                check_out_photo_url: item.check_out_photo_url || "",
                CheckOutLocation: item.CheckOutLocation || "",
                CheckOutWorkSite: item.CheckOutWorkSite || "",
                FirstInType: item.FirstInType || "",
                LastOutType: item.LastOutType || "",
                check_in_latitude: item.check_in_latitude || null,
                check_in_longitude: item.check_in_longitude || null,
                check_out_latitude: item.sales_checkout_latitude || item.check_out_latitude || null,
                check_out_longitude: item.sales_checkout_longitude || item.check_out_longitude || null,
                sales_checkout_remarks: item.sales_checkout_remarks || null,
                OndutyTypeCheckIn: item.OndutyTypeCheckIn || null,
                OndutyTypeCheckOut: item.OndutyTypeCheckOut || null,
                Remarks: item.Status || "",
            }));

            setNormalizedMembers(cleaned);
            setFilteredMembers(cleaned);
            setPagination((prev) => ({ ...prev, current: 1 }));

        } catch (err) {
            console.error("Failed to fetch attendance", err);
        } finally {
            setLoading(false);
        }
    }, [filters, buildQueryParams, authData, backUrl]);

    useEffect(() => {
        fetchOrganisations();
    }, []);

    useEffect(() => {
        if (authData?.employee?.id) {
            fetchAttendanceData();
        }
    }, [authData, fetchAttendanceData]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => {
            const updated = { ...prev, [key]: value };

            if (key === "organisation") {
                updated.department = "";
                updated.designation = "";
                fetchDepartments(value);
                setDesignations([]);
            }
            if (key === "department") {
                updated.designation = "";
                fetchDesignations(value);
            }
            // Clear remarks when status changes away from "present"
            if (key === "status" && value !== "present") {
                updated.remarks = "";
            }

            return updated;
        });
    };

    const applyFilters = () => {
        fetchAttendanceData(filters);
    };

    const clearFilters = () => {
        const empty = {
            name: "",
            empCode: "",
            status: "",
            organisation: "",
            department: "",
            designation: "",
            fromDate: null,
            toDate: null,
            remarks: "",  // ← fixed: was missing in original
        };
        setFilters(empty);
        setDepartments([]);
        setDesignations([]);
        fetchAttendanceData(empty);
    };

    const handleDownloadExcel = () => {
        const exportData =
            selectedRowKeys.length > 0
                ? filteredMembers.filter((item) => selectedRowKeys.includes(item.EmployeeCode))
                : filteredMembers;

        if (exportData.length === 0) return alert("No data to export!");

        const statusColorMap = {
            present: { fgColor: { rgb: "C6EFCE" } },
            absent: { fgColor: { rgb: "FFC7CE" } },
            weekoff: { fgColor: { rgb: "FFF2CC" } },
            permission: { fgColor: { rgb: "E2EFDA" } },
            halfday_leave: { fgColor: { rgb: "EAD1DC" } },
            sick: { fgColor: { rgb: "DDEBF7" } },
            casual: { fgColor: { rgb: "EAD1DC" } },
        };

        const normalizeStatus = (status, dateStr) => {
            if (!status) return "–";
            const lower = String(status).trim().toLowerCase();
            if (["present", "absent", "weekoff", "permission", "halfday_leave", "sick", "casual"].includes(lower)) {
                return lower;
            }
            if (dateStr && dayjs(dateStr).day() === 0) return "weekoff";
            return lower;
        };

        const formattedData = exportData.map((item) => {
            const row = {};
            allColumns.forEach((col) => {
                if (visibleColumns.includes(col.key)) {
                    let value = item[col.key];
                    if (col.key === "Remarks") value = formatRemarks(value);
                    if (col.key === "Date" && value) value = dayjs(value).format("DD/MM/YYYY");
                    if (col.key === "AttendanceStatus") value = normalizeStatus(value, item.Date);
                    row[col.title] = value || "–";
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const headers = Object.keys(formattedData[0] || {});

        headers.forEach((_, i) => {
            const cell = XLSX.utils.encode_cell({ r: 0, c: i });
            worksheet[cell].s = {
                fill: { patternType: "solid", fgColor: { rgb: "0E497B" } },
                font: { bold: true, color: { rgb: "FFFFFF" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } },
            };
        });

        formattedData.forEach((row, rIdx) => {
            const status = row["Status"]?.toLowerCase().trim();
            const fill = statusColorMap[status]?.fgColor || { rgb: "FFFFFF" };
            headers.forEach((_, cIdx) => {
                const cell = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
                if (!worksheet[cell]) worksheet[cell] = { v: "", t: "s" };
                worksheet[cell].s = {
                    fill: { patternType: "solid", fgColor: fill },
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "CCCCCC" } },
                        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                        left: { style: "thin", color: { rgb: "CCCCCC" } },
                        right: { style: "thin", color: { rgb: "CCCCCC" } },
                    },
                };
            });
        });

        worksheet["!cols"] = headers.map((h) => ({
            wch: Math.min(Math.max(h.length, ...formattedData.map((r) => String(r[h] || "").length)), 40) + 4,
        }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, worksheet, "Team Attendance");
        XLSX.writeFile(wb, "TeamAttendance.xlsx", { cellStyles: true });
    };

    const handleColumnVisibilityChange = (key, e) => {
        e.stopPropagation();
        setVisibleColumns((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const handleSelectAllColumns = (e) => {
        e.stopPropagation();
        setVisibleColumns(allColumns.map((c) => c.key));
    };

    const handleDeselectAllColumns = (e) => {
        e.stopPropagation();
        setVisibleColumns([]);
    };

    const columnSelectorMenu = {
        items: [
            {
                key: "actions",
                label: (
                    <div style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0", marginBottom: 8 }}>
                        <Button size="small" type="link" onClick={handleSelectAllColumns} style={{ color: "#0e497b" }}>
                            Select All
                        </Button>
                        <Button size="small" type="link" onClick={handleDeselectAllColumns} style={{ color: "#0e497b" }}>
                            Deselect All
                        </Button>
                        <Button
                            size="small"
                            icon={<CloseCircleOutlined />}
                            type="primary"
                            onClick={() => setDropdownOpen(false)}
                            style={{ float: "right", backgroundColor: "white", color: "#0e497b" }}
                        />
                    </div>
                ),
            },
            ...allColumns.map((col) => ({
                key: col.key,
                label: (
                    <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        onChange={(e) => handleColumnVisibilityChange(col.key, e)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {col.title}
                    </Checkbox>
                ),
            })),
        ],
    };

    const getTableColumns = () => {
        const defs = {
            EmployeeCode: { title: "Employee Code", dataIndex: "EmployeeCode", render: (v) => v || "–" },
            EmployeeName: { title: "Employee Name", dataIndex: "EmployeeName", render: (v) => v || "–" },
            Organisation: { title: "Organisation", dataIndex: "Organisation", render: (v) => v || "–" },
            Department: { title: "Department", dataIndex: "Department", render: (v) => v || "–" },
            Designation: { title: "Designation", dataIndex: "Designation", render: (v) => v || "–" },
            Date: { title: "Date", dataIndex: "Date", render: (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "–") },
            AttendanceStatus: { title: "Status", dataIndex: "AttendanceStatus", render: (v) => v || "–" },
            Remarks: {
                title: "Remarks",
                dataIndex: "Remarks",
                render: (v) => formatRemarks(v),
            },
            site_location: { title: "Work Location", dataIndex: "site_location", render: (v) => v || "–" },
            PunchIn: { title: "Punch In", dataIndex: "PunchIn", render: (v) => v || "–" },
            CheckIn: { title: "Check In", dataIndex: "CheckIn", render: (v) => v || "–" },
            check_in_photo_url: {
                title: "Check In Photo",
                dataIndex: "check_in_photo_url",
                render: (url) =>
                    url ? (
                        <Image
                            width={80}
                            height={80}
                            src={url}
                            style={{ objectFit: "cover", borderRadius: 6 }}
                            preview={{ mask: "Preview" }}
                        />
                    ) : "–",
            },
            CheckInLocation: {
                title: "Check-In Location",
                render: (_, record) =>
                    record.check_in_latitude && record.check_in_longitude ? (
                        <a
                            href={`https://www.google.com/maps?q=${record.check_in_latitude},${record.check_in_longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1677ff" }}
                        >
                            {record.CheckInLocation || "View Location"}
                        </a>
                    ) : (
                        record.CheckInLocation || "–"
                    ),
            },
            CheckInWorkSite: {
                title: "Check In Site",
                render: (_, r) => (
                    <>
                        <p style={{ marginBottom: (r.OndutyTypeCheckIn || r.lead_data || r.lead_details || r.external_lead_details) ? 4 : 0 }}>
                            {r.CheckInWorkSite || "–"}
                        </p>
                        {r.OndutyTypeCheckIn && <p style={{ marginBottom: (r.lead_data || r.lead_details || r.external_lead_details) ? 4 : 0, fontSize: 12, color: "#666" }}>({r.OndutyTypeCheckIn})</p>}
                        {(r.lead_data || r.lead_details || r.external_lead_details) && (
                            <a
                                onClick={() => {
                                    const raw = r.lead_data || r.lead_details || r.external_lead_details;
                                    let parsed = raw;
                                    if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { } }
                                    const arr = Array.isArray(parsed) ? parsed : [parsed];
                                    setSelectedLeadDetails(arr);
                                    setShowLead(true);
                                }}
                                style={{ color: "#1677ff", cursor: "pointer", fontSize: "12px" }}
                            >
                                Lead Details
                            </a>
                        )}
                    </>
                ),
            },
            PunchOut: { title: "Punch Out", dataIndex: "PunchOut", render: (v) => v || "–" },
            CheckOut: { title: "Check Out", dataIndex: "CheckOut", render: (v) => v || "–" },
            check_out_photo_url: {
                title: "Check Out Photo",
                dataIndex: "check_out_photo_url",
                render: (url) =>
                    url ? (
                        <Image
                            width={80}
                            height={80}
                            src={url}
                            style={{ objectFit: "cover", borderRadius: 6 }}
                            preview={{ mask: "Preview" }}
                        />
                    ) : "–",
            },
            CheckOutLocation: {
                title: "Check-Out Location",
                render: (_, record) => (
                    <div>
                        {record.check_out_latitude && record.check_out_longitude ? (
                            <a
                                href={`https://www.google.com/maps?q=${record.check_out_latitude},${record.check_out_longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1677ff" }}
                            >
                                {record.CheckOutLocation || "View Location"}
                            </a>
                        ) : (
                            record.CheckOutLocation || "-"
                        )}
                        {record.sales_checkout_remarks && (
                            <div style={{
                                marginTop: 4,
                                padding: "2px 8px",
                                background: "#fff7e6",
                                border: "1px solid #ffa940",
                                borderRadius: 4,
                                fontSize: 11,
                                color: "#d46b08",
                                fontWeight: 500,
                            }}>
                                ⚠ {record.sales_checkout_remarks}
                            </div>
                        )}
                    </div>
                ),
            },
            CheckOutWorkSite: {
                title: "Check Out Site",
                render: (_, r) => (
                    <>
                        <p style={{ marginBottom: (r.OndutyTypeCheckOut || r.lead_data || r.lead_details || r.external_lead_details) ? 4 : 0 }}>
                            {r.CheckOutWorkSite || "–"}
                        </p>
                        {r.OndutyTypeCheckOut && <p style={{ marginBottom: (r.lead_data || r.lead_details || r.external_lead_details) ? 4 : 0, fontSize: 12, color: "#666" }}>({r.OndutyTypeCheckOut})</p>}
                        {(r.lead_data || r.lead_details || r.external_lead_details) && (
                            <a
                                    onClick={() => {
                                    const raw = r.lead_data || r.lead_details || r.external_lead_details;
                                    let parsed = raw;
                                    if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { } }
                                    const arr = Array.isArray(parsed) ? parsed : [parsed];
                                    setSelectedLeadDetails(arr);
                                    setShowLead(true);
                                }}
                                style={{ color: "#1677ff", cursor: "pointer", fontSize: "12px" }}
                            >
                                Lead Details
                            </a>
                        )}
                    </>
                ),
            },
            FirstInType: { title: "First In", dataIndex: "FirstInType", render: (v) => v || "–" },
            LastOutType: { title: "Last Out", dataIndex: "LastOutType", render: (v) => v || "–" },
        };

        return visibleColumns.filter((key) => defs[key]).map((key) => defs[key]);
    };

    return (
        <>
            {showLead && (
                <Modal
                    open={showLead}
                    footer={null}
                    onCancel={() => {
                        setShowLead(false);
                        setSelectedLeadDetails(null);
                    }}
                    width={700}
                >
                    {selectedLeadDetails && selectedLeadDetails.length > 0 ? (
                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            {selectedLeadDetails.map((lead, index) => (
                                <Descriptions
                                    key={index}
                                    bordered
                                    column={1}
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                    title={selectedLeadDetails.length > 1 ? `Lead ${index + 1}` : null}
                                >
                                    <Descriptions.Item label="Lead Number">
                                        <strong>{lead?.lead_no || lead?.lead_id || "-"}</strong>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Customer Name">
                                        {lead?.customer_name || lead?.name || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Email">
                                        {lead?.email || lead?.customer_email || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Phone Number">
                                        {lead?.number || lead?.phone || lead?.customer_phone || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Project Name">
                                        {lead?.project_name || lead?.project_id || (lead?.project && lead.project.title) || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Status">
                                        <span style={{
                                            padding: "4px 12px",
                                            borderRadius: "4px",
                                            backgroundColor: "#f6ffed",
                                            color: lead?.status?.toLowerCase().includes("cancel") ? "#d46b08" : "#52c41a",
                                            fontWeight: 500,
                                            border: "1px solid #b7eb8f"
                                        }}>
                                            {lead?.status?.title || lead?.status || "-"}
                                        </span>
                                    </Descriptions.Item>
                                </Descriptions>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                            No lead details available
                        </div>
                    )}
                </Modal>
            )}

            {!isHomePage && (
                <center>
                    <h5 style={{ color: "#0e497b" }}>Team Attendance</h5>
                </center>
            )}

            <div style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <Row gutter={[12, 12]} align="middle">
                    {!isHomePage && (
                        <>
                            <Col xs={24} sm={12} md={6} lg={4}>
                                <div style={{ textAlign: "center", marginBottom: 4 }}>Employee Name</div>
                                <Input
                                    placeholder="Search name..."
                                    value={filters.name}
                                    onChange={(e) => handleFilterChange("name", e.target.value)}
                                    allowClear
                                />
                            </Col>

                            <Col xs={24} sm={12} md={6} lg={4}>
                                <div style={{ textAlign: "center", marginBottom: 4 }}>Employee Code</div>
                                <Input
                                    placeholder="Search code..."
                                    value={filters.empCode}
                                    onChange={(e) => handleFilterChange("empCode", e.target.value)}
                                    allowClear
                                />
                            </Col>

                            <Col xs={24} sm={12} md={6} lg={4}>
                                <div style={{ textAlign: "center", marginBottom: 4 }}>Status</div>
                                <Select
                                    placeholder="Select status"
                                    value={filters.status || undefined}
                                    onChange={(v) => handleFilterChange("status", v ?? "")}
                                    allowClear
                                    style={{ width: "100%" }}
                                >
                                    <Option value="present">Present</Option>
                                    <Option value="absent">Absent</Option>
                                    <Option value="weekoff">Week Off</Option>
                                    <Option value="permission">Permission</Option>
                                    <Option value="halfday_leave">Half Day</Option>
                                    <Option value="sick">Sick Leave</Option>
                                    <Option value="casual">Casual Leave</Option>
                                </Select>
                            </Col>

                            {filters.status === "present" && (
                                <Col xs={24} sm={12} md={6} lg={4}>
                                    <div style={{ textAlign: "center", marginBottom: 4 }}>Remarks</div>
                                    <Select
                                        placeholder="Select Remarks"
                                        value={filters.remarks || undefined}
                                        onChange={(v) => handleFilterChange("remarks", v ?? "")}
                                        allowClear
                                        style={{ width: "100%" }}
                                    >
                                        {/* Values match REMARK_VALUE_MAP keys in the backend */}
                                        <Option value="latein">Late In</Option>
                                        <Option value="earlyout">Early Out</Option>
                                    </Select>
                                </Col>
                            )}
                        </>
                    )}

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div style={{ textAlign: "center", marginBottom: 4 }}>Organisation</div>
                        <Select
                            placeholder="Select Organisation"
                            value={filters.organisation || undefined}
                            onChange={(v) => handleFilterChange("organisation", v ?? "")}
                            allowClear
                            style={{ width: "100%" }}
                        >
                            {organisations.map((o) => (
                                <Option key={o.id} value={o.id}>{o.name}</Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div style={{ textAlign: "center", marginBottom: 4 }}>Department</div>
                        <Select
                            showSearch
                            placeholder="Select Department"
                            value={filters.department || undefined}
                            onChange={(v) => handleFilterChange("department", v ?? "")}
                            disabled={!filters.organisation}
                            filterOption={(input, option) =>
                                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                        >
                            {departments.map((d) => (
                                <Option key={d.id} value={d.id}>{d.name}</Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div style={{ textAlign: "center", marginBottom: 4 }}>Designation</div>
                        <Select
                            showSearch
                            placeholder="Select Designation"
                            value={filters.designation || undefined}
                            onChange={(v) => handleFilterChange("designation", v ?? "")}
                            disabled={!filters.department}
                            filterOption={(input, option) =>
                                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                        >
                            {designations.map((ds) => (
                                <Option key={ds.id} value={ds.id}>{ds.name}</Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div style={{ textAlign: "center", marginBottom: 4 }}>Date Range</div>
                        <RangePicker
                            value={[filters.fromDate, filters.toDate]}
                            format="DD/MM/YYYY"
                            onChange={(dates) => {
                                handleFilterChange("fromDate", dates?.[0] || null);
                                handleFilterChange("toDate", dates?.[1] || null);
                            }}
                            allowClear
                            style={{ width: "100%" }}
                        />
                    </Col>

                    <Col>
                        <div style={{ marginTop: 28 }}>
                            <Button
                                type="primary"
                                onClick={applyFilters}
                                style={{ marginRight: 8, backgroundColor: "#0e497b", color: "white" }}
                            >
                                Apply Filters
                            </Button>
                            <Button onClick={clearFilters}>Clear</Button>
                        </div>
                    </Col>

                    <Col>
                        <Tooltip title="Download Excel">
                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={handleDownloadExcel}
                                style={{ backgroundColor: "#107c41", borderColor: "#107c41", marginTop: 28 }}
                            >
                                Excel
                            </Button>
                        </Tooltip>
                    </Col>
                </Row>

                <Row justify="end" style={{ marginTop: 12 }}>
                    <Col>
                        <Dropdown
                            menu={columnSelectorMenu}
                            trigger={["click"]}
                            placement="bottomRight"
                            open={dropdownOpen}
                            onOpenChange={setDropdownOpen}
                        >
                            <Tooltip title="Select Columns">
                                <Button icon={<ColumnHeightOutlined />}>Custom Columns</Button>
                            </Tooltip>
                        </Dropdown>
                    </Col>
                </Row>
            </div>

            {!isHomePage && (
                <Table
                    rowSelection={{
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                    }}
                    dataSource={filteredMembers}
                    columns={getTableColumns()}
                    rowKey={(_, index) => index}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "30", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
                        onShowSizeChange: (_, size) => setPagination({ current: 1, pageSize: size }),
                    }}
                    scroll={{ x: "max-content" }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                />
            )}
        </>
    );
}